/**
 * crm-adapter.ts  (v2)
 *
 * React bridge for the CrmPlugin.
 * Exposes useSyncExternalStore hooks for CrmState v2 (Map-based).
 */

import { useSyncExternalStore, useMemo } from 'react';
import type { CrmState, CrmCommand } from './crm-contracts.js';
import { InMemoryCrmStore } from './crm-store.js';
import { CrmQueryEngine } from './crm-query-engine.js';
import type { ContactFilter, DealFilter, ActivityFilter } from './crm-query-engine.js';

let globalStore: InMemoryCrmStore | null = null;

export function mountCrmStore(store: InMemoryCrmStore): void {
  globalStore = store;
}

function requireStore(): InMemoryCrmStore {
  if (!globalStore) {
    globalStore = new InMemoryCrmStore('crm');
  }
  return globalStore;
}

export function useCrmState(): CrmState {
  const store = requireStore();
  return useSyncExternalStore(
    store.subscribe.bind(store),
    store.getSnapshot.bind(store),
    store.getSnapshot.bind(store),
  );
}

export function useCrmDispatch() {
  return (command: CrmCommand) => requireStore().dispatch(command);
}

export function useCrmContacts(filter: ContactFilter = {}) {
  const state = useCrmState();
  return useMemo(() => new CrmQueryEngine(state).queryContacts(filter), [state, JSON.stringify(filter)]);
}

export function useCrmOrganizations(query?: string) {
  const state = useCrmState();
  return useMemo(() => new CrmQueryEngine(state).queryOrganizations(query), [state, query]);
}

export function useCrmDealsByStage(filter: DealFilter = {}) {
  const state = useCrmState();
  return useMemo(() => new CrmQueryEngine(state).queryDealsByStage(), [state, JSON.stringify(filter)]);
}

export function useCrmActivities(filter: ActivityFilter = {}) {
  const state = useCrmState();
  return useMemo(() => new CrmQueryEngine(state).queryActivities(filter), [state, JSON.stringify(filter)]);
}

export function useCrmMetrics() {
  const state = useCrmState();
  return useMemo(() => new CrmQueryEngine(state).summaryMetrics(), [state]);
}

export function useCrmContact(contactId: string | undefined) {
  const state = useCrmState();
  return useMemo(
    () => (contactId ? new CrmQueryEngine(state).findContact(contactId) : undefined),
    [state, contactId],
  );
}

export function useCrmOrganization(orgId: string | undefined) {
  const state = useCrmState();
  return useMemo(
    () => (orgId ? new CrmQueryEngine(state).findOrganization(orgId) : undefined),
    [state, orgId],
  );
}

export function useCrmDeal(dealId: string | undefined) {
  const state = useCrmState();
  return useMemo(
    () => (dealId ? new CrmQueryEngine(state).findDeal(dealId) : undefined),
    [state, dealId],
  );
}

export function useCrmStorageHealth() {
  const store = requireStore();
  return store.getStorageHealth();
}
